<template>
  <c3-chart v-if="loaded" ref="chart" :height="height" :data="chartData" :config="chartConfig" type='area' />
  <v-skeleton-loader v-else type="image" :height="height"/>
</template>

<script>
import C3Chart from '@/components/shared/chart/C3Chart'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import { get, map, max, min, round, some } from 'lodash'
import Moment from 'moment-timezone'
import MathUtil from '@/util/MathUtil'

export default {
  name: 'RoutePlansChart',
  components: {
    C3Chart
  },
  mixins: [
    CurrentUserMixin
  ],
  props: {
    selectedCustomers: {
      type: Array,
      default () {
        return []
      }
    },
    height: {
      type: Number,
      default: 200
    }
  },
  data () {
    return {
      loaded: false,
      reportData: null
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    /**
     * Expose internal chart fit method, so we can redraw on demand
     */
    fit () {
      this.$refs.chart.fit()
    },
    /**
     * Load the chart data from the API
     */
    async loadData () {
      this.loaded = false
      try {
        const customerIds = map(this.selectedCustomers, x => x.id)
        const start = Moment.tz(this.customerTimezone).subtract(6, 'months').format()
        const end = Moment.tz(this.customerTimezone).format()
        const result = await this.$httpAdapter.get('/customer-insights/route-plan-history', {
          params: {
            customer_id: customerIds.join(','),
            start: start,
            end: end
          }
        })
        this.reportData = get(result.data, 'combined.data')
        this.reportData = this.filterData(this.reportData)
        this.loaded = true
      } catch (error) {
        this.handleError(error)
      }
    },
    /**
     * On days with no route plans, we can't calculate proper percentages, so we reset the displayed percentages to 0%
     * by default. This alarms some users, because it sticks out in the charts and appears to affect the primary score
     * metric (it doesn't in reality, unless there's no data for the *entire* period.)
     * Instead of showing 0%, we should show a gap in the data, since no data on a particular day is probably of
     * interest to the users. To do this, we just reset the display values to null where applicable.
     * @param data
     * @returns {Array}
     */
    filterData (data) {
      return map(data, x => {
        if (get(x, 'total_route_plans') === 0) {
          x.total_route_plans = null
          x.linked_route_plans = null
        }
        if (get(x, 'planned_stops') === 0) {
          x.planned_stops = null
          x.missed_stops = null
        }
        return x
      })
    }
  },
  computed: {
    /**
     * Format the chart data needed for the graph.
     * @returns Object
     */
    chartData () {
      const dateColumn = map(this.reportData, x => x.date)
      dateColumn.unshift('date')

      const linkedRoutePlansColumn = map(this.reportData, x => {
        if (x.linked_route_plans == null) {
          return null
        }
        return MathUtil.percentage(x.linked_route_plans, x.total_route_plans)
      })
      linkedRoutePlansColumn.unshift('linkedRoutePlans')

      // We pivoted from showing missed stops to completed stops. We're not storing completed stops data in the history,
      // but we can easily get it just by subtracting the percentage of missed stops from 100.
      const completedStopsColumn = map(this.reportData, x => {
        if (x.planned_stops == null || x.planned_stops === 0) {
          return null
        }
        return 100 - MathUtil.percentage(x.missed_stops, x.planned_stops)
      })
      completedStopsColumn.unshift('completedStops')

      return {
        x: 'date',
        columns: [
          dateColumn,
          linkedRoutePlansColumn,
          completedStopsColumn
        ],
        names: {
          linkedRoutePlans: this.$tc('linked_route_plan', 2),
          completedStops: this.$tc('completed_stop', 2)
        },
        hide: ['completedStops'],
        colors: {
          linkedRoutePlans: '#195152',
          completedStops: '#7C931F'
        }
      }
    },
    /**
     * Set the range for the chart. If any values fall below 60, show the full range, otherwise truncate to 60.
     * @returns {number[]}
     */
    rangeValues () {
      const linkedPlans = some(this.chartData.columns[1], x => x != null && x < 60)
      const completedStops = some(this.chartData.columns[2], x => x != null && x < 60)
      if (linkedPlans || completedStops) {
        return [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]
      } else {
        return [60, 70, 80, 90, 100]
      }
    },
    /**
     * The chart configuration.
     * @returns Object
     */
    chartConfig () {
      return {
        point: {
          show: false
        },
        axis: {
          x: {
            type: 'timeseries',
            tick: {
              count: 12,
              format: '%Y-%m-%d',
              centered: true
            }
          },
          y: {
            label: {
              text: this.$t('percentage')
            },
            tick: {
              values: this.rangeValues,
              format: function (d) {
                return round(d, 1) + '%'
              },
              culling: true
            },
            min: min(this.rangeValues),
            max: max(this.rangeValues)
          }
        },
        line: {
          connectNull: true
        }
      }
    }
  }
}
</script>

<style scoped>

</style>
